import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Table,
  Tag,
  Button,
  Input,
  Popconfirm,
  message,
} from 'antd';
import { PlusCircleOutlined } from '@ant-design/icons';
import { getOrganizationTeams, deleteTeam } from '../../../actions/teams';
import { openModal } from '../../../actions/modal';
import { captureAnalytics } from '../../../actions/preload';

const { Search } = Input;

const ManageTeams = (props) => {
  const {
    teams,
    isLoading,
    getOrganizationTeams: fetchTeams,
    deleteTeam: removeTeam,
    openModal: showModal,
  } = props;
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    fetchTeams();
  }, []);

  const openAddTeamModal = (team = null) => {
    captureAnalytics('team_add_modal_opened', { is_edit: !!team });
    showModal({
      type: 'ADD_TEAM',
      data: { team },
    });
  };

  const handleDelete = async (team) => {
    // The team serializer exposes the slug as `id` (see Team#object).
    const result = await removeTeam(team.id);
    if (result && result.error) {
      const errorMessage = result.response?.data?.error || 'Error deleting team. Please try again.';
      message.error({ content: errorMessage });
    } else {
      captureAnalytics('team_deleted');
      message.success({ content: 'Team successfully deleted' });
    }
    fetchTeams();
  };

  const visibleTeams = teams.filter(
    (team) => team.name.toLowerCase().includes(searchValue.trim().toLowerCase()),
  );

  const columns = [
    {
      title: 'Team',
      key: 'name',
      render: (_, team) => (
        <Tag
          style={{
            backgroundColor: team.color,
            color: team.is_dark_text ? 'black' : 'white',
            borderColor: team.color,
          }}
        >
          {team.name}
        </Tag>
      ),
    },
    {
      title: 'Members',
      dataIndex: 'members_count',
      key: 'members_count',
      render: (count) => count ?? 0,
    },
    {
      title: 'Tagged documents',
      dataIndex: 'tagged_documents_count',
      key: 'tagged_documents_count',
      render: (count) => count ?? 0,
    },
    {
      title: 'Cross-department visibility',
      key: 'cross_department_visibility',
      render: (_, team) => (team.cross_department_visibility ? 'On' : 'Off'),
    },
    {
      title: '',
      key: 'actions',
      align: 'left',
      render: (_, team) => (
        <span>
          <Button onClick={() => openAddTeamModal(team)} style={{ marginRight: 10 }}>
            Edit
          </Button>
          <Popconfirm
            title="Delete this team? (This action is permanent and cannot be reversed)"
            onConfirm={() => handleDelete(team)}
          >
            <a>Delete</a>
          </Popconfirm>
        </span>
      ),
    },
  ];

  return (
    // eslint-disable-next-line object-curly-newline
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', padding: '16px 40px 40px', textAlign: 'left' }}>
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4, marginBottom: 16,
      }}
      >
        <h2 style={{
          fontSize: 22, fontWeight: 600, color: '#181818', margin: 0, textAlign: 'left',
        }}
        >
          Manage Teams
        </h2>
        <p style={{
          color: '#5c5c5c', fontSize: 13, lineHeight: 1.5, margin: 0, textAlign: 'left',
        }}
        >
          Group users into teams to tag documents with a shared, color-coded
          label — optionally granting members visibility across departments.
        </p>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <Button
          icon={<PlusCircleOutlined />}
          type="primary"
          onClick={() => openAddTeamModal()}
        >
          Add Team
        </Button>
        <div>
          <Search
            placeholder="search teams"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onSearch={(value) => setSearchValue(value)}
            style={{ width: 200, height: '32px', marginRight: '10px' }}
          />
        </div>
      </div>
      <div style={{ width: '100%' }}>
        <Table
          size="small"
          loading={isLoading}
          dataSource={visibleTeams}
          columns={columns}
          rowKey={(team) => team.id}
          bordered
          pagination={{ position: ['bottomCenter'] }}
        />
      </div>
    </div>
  );
};

ManageTeams.defaultProps = {
  teams: [],
  isLoading: false,
};

ManageTeams.propTypes = {
  teams: PropTypes.array,
  isLoading: PropTypes.bool,
  getOrganizationTeams: PropTypes.func.isRequired,
  deleteTeam: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
};

export default connect((state) => ({
  teams: state.teams.data,
  isLoading: state.teams.isLoading,
}), {
  getOrganizationTeams,
  deleteTeam,
  openModal,
})(ManageTeams);

export {
  ManageTeams,
};
